import React, { ReactElement, ReactNode } from 'react';

export interface IMetaProps {
  title?: string;
  keywords?: string;
  description?: string;
  siteName?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogImgUrl?: string;
  children?: ReactNode;
}

// A helper component that allows you to easily change the meta of a page
// such as the browser title or og meta image.
//
// This renders into Gatsby's Head API, so it must only ever be used inside a
// `export const Head` of a page or template — never in the page body. Head is
// rendered in its own React tree, so it has no access to context, hooks or
// static queries.
export default function Meta({
  title = 'Stark: The suite of integrated accessibility tools',
  keywords = 'stark, accessibility, color-blind, contrast, check, plug-in, plugin, sketch',
  description = 'The suite of integrated accessibility tools for your product design and development team • Making the world’s products accessible.',
  siteName = 'Stark',
  ogType = 'website',
  ogTitle = 'Stark: The suite of integrated accessibility tools for your product design and development team',
  ogDescription,
  ogUrl,
  ogImgUrl,
  children,
}: IMetaProps): ReactElement {
  return (
    <>
      <title>{title}</title>

      <meta name="keywords" content={keywords} />

      <meta name="author" content="Stark Lab, Inc" />

      <meta name="description" content={description} />

      <meta property="og:site_name" content={siteName} />

      <meta property="og:type" content={ogType} />

      <meta
        property="og:description"
        content={ogDescription ? ogDescription : description}
      />

      <meta name="twitter:description" content={ogDescription} />

      <meta name="twitter:site" content="@getstarkco" />

      {ogTitle && (
        <>
          <meta property="og:title" content={ogTitle} />

          <meta name="twitter:title" content={ogTitle} />
        </>
      )}

      {ogUrl && (
        <>
          <meta property="og:url" content={ogUrl} />

          <meta name="twitter:url" content={ogUrl} />
        </>
      )}

      {ogImgUrl && (
        <>
          <meta name="twitter:card" content="summary_large_image" />

          <meta property="og:image" content={ogImgUrl} />

          <meta name="twitter:image" content={ogImgUrl} />
        </>
      )}

      {children}
    </>
  );
}
